<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingMappings">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div v-for="location in orderedLocations" :key="location.businessLocationId" class="tw-flex tw-items-center tw-mb-4">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <base-checkbox :value="location.businessLocationId" v-model="selectedLocations" :label="location.name" />
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-select
          class="tw-flex-grow"
          :options="orderedPlans"
          label-key="name"
          value-key="businessPlanId"
          placeholder="Select a plan"
          :value="mappings[location.businessLocationId]"
          @input="handleInput(location.businessLocationId, $event)"
        />
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between">
        <base-button type="flat" @click="selectAll">{{ areAllSelected ? "Deselect All" : "Select All" }}</base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()"> Cancel </base-button>
          <base-button :loading="isSaving" class="tw-w-auto" @click="save"> Save </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";

export default {
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingMappings: false,
      isSaving: false,
      businessProgram: {},
      areLocationsLoading: false,
      arePlansLoading: false,
      locations: [],
      plans: [],
      selectedLocations: [],
      mappings: {},
    };
  },
  computed: {
    isLoading() {
      return this.areLocationsLoading || this.arePlansLoading;
    },
    title() {
      return `${this.businessProgram.name} Mappings`;
    },
    orderedLocations() {
      return orderBy(this.locations, ["name"], ["asc"]);
    },
    orderedPlans() {
      return orderBy(this.plans, ["name"], ["asc"]);
    },
    areAllSelected() {
      return this.locations.every((l) => !!this.selectedLocations.find((sl) => sl === l.businessLocationId));
    },
  },
  methods: {
    show(businessProgram) {
      this.businessProgram = businessProgram;
      this.$refs.slideover.show();
      this.selectedLocations = [];
      this.getMappings();

      if (this.locations.length === 0) {
        this.getLocations();
      }

      if (this.plans.length === 0) {
        this.getPlans();
      }
    },
    hide() {
      this.$refs.slideover.hide();
    },
    async getLocations() {
      try {
        this.areLocationsLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/all`);
        this.locations = response.data;
      } finally {
        this.areLocationsLoading = false;
      }
    },
    async getMappings() {
      try {
        this.isLoadingMappings = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}/mappings`);
        this.mappings = {};
        response.data.forEach((m) => {
          this.mappings[m.businessLocationId] = m.businessPlanId;
        });
        this.$forceUpdate();
      } finally {
        this.isLoadingMappings = false;
      }
    },
    async getPlans() {
      try {
        this.arePlansLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } finally {
        this.arePlansLoading = false;
      }
    },
    async save() {
      try {
        this.isSaving = true;
        await this.$axios.post(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}/mappings`, {
          mappings: this.mappings,
        });
        this.hide();
        this.selectedLocations = [];
        this.mappings = {};
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save mappings",
          message: `We were not able to save the mappings for this prescription program. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(businessLocationId, businessPlanId) {
      if (this.selectedLocations.includes(businessLocationId)) {
        this.selectedLocations.forEach((locationId) => {
          this.mappings[locationId] = businessPlanId;
        });
      } else {
        this.mappings[businessLocationId] = businessPlanId;
      }
      this.$forceUpdate();
    },
    selectAll() {
      if (this.areAllSelected) {
        this.selectedLocations = [];
      } else {
        this.selectedLocations = this.locations.map((l) => l.businessLocationId);
      }
    },
  },
};
</script>
